import { useEffect, useState, useRef } from "react";
import { ArrowLeft, Send, Paperclip, ChevronRight, ChevronLeft, ChevronDown, Clock, Pin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
// Simple collapsible components since we don't have the UI components
const Collapsible = ({ children, className = "" }: { children: React.ReactNode, className?: string }) => (
  <div className={className}>{children}</div>
);

const CollapsibleTrigger = ({ children, asChild = false, className = "" }: { children: React.ReactNode, asChild?: boolean, className?: string }) => (
  <div className={className}>{children}</div>
);

const CollapsibleContent = ({ children, className = "" }: { children: React.ReactNode, className?: string }) => (
  <div className={className}>{children}</div>
);
// Create a simple separator component since we don't have the UI component
const Separator = () => <hr className="border-t border-gray-200 my-3" />;

// Sample conversation data
const conversationData = [
  {
    id: 1,
    sender: "CompetitorBot",
    avatarSrc: "/crew-agents/competitors.png",
    message: "Good morning! I've been monitoring our top competitors over the weekend and noticed some significant updates I think you should be aware of.",
    timestamp: "Monday, 9:05 AM",
    isUser: false
  },
  {
    id: 2,
    sender: "You",
    avatarSrc: "/crew-agents/page-semantic.png",
    message: "Good morning! What did you find?",
    timestamp: "Monday, 9:10 AM",
    isUser: true
  },
  {
    id: 3,
    sender: "CompetitorBot",
    avatarSrc: "/crew-agents/competitors.png",
    message: "Competitor A has launched a new version of their analytics dashboard with three key features we don't currently offer:\n\n1. Real-time collaboration on reports\n2. AI-powered anomaly detection\n3. Custom visualization builder\n\nTheir users are responding very positively to these changes based on social media sentiment analysis.",
    timestamp: "Monday, 9:12 AM",
    isUser: false,
    isPinned: true
  },
  {
    id: 4,
    sender: "You",
    avatarSrc: "/crew-agents/page-semantic.png",
    message: "That's concerning. How long would it take us to implement similar capabilities?",
    timestamp: "Monday, 9:15 AM",
    isUser: true
  },
  {
    id: 5,
    sender: "CompetitorBot",
    avatarSrc: "/crew-agents/competitors.png",
    message: "I've consulted with the development team, and they estimate:\n\n- Real-time collaboration: 3-4 weeks\n- Basic anomaly detection: 2 weeks\n- Custom visualization builder: 4-6 weeks\n\nWould you like me to prepare a detailed implementation plan with resource requirements?",
    timestamp: "Monday, 9:20 AM",
    isUser: false
  },
  {
    id: 6,
    sender: "You",
    avatarSrc: "/crew-agents/page-semantic.png",
    message: "Yes, please do. Also, have any of our other competitors implemented similar features?",
    timestamp: "Monday, 9:22 AM",
    isUser: true
  },
  {
    id: 7,
    sender: "CompetitorBot",
    avatarSrc: "/crew-agents/competitors.png",
    message: "Yes, Competitor C has had real-time collaboration for about 3 months, but their implementation is limited to comments only, not live editing. No other main competitors have implemented anomaly detection or custom visualization builders yet, though I've seen mentions of them in Competitor B's product roadmap documents.",
    timestamp: "Monday, 9:25 AM",
    isUser: false
  },
  {
    id: 8,
    sender: "CompetitorBot",
    avatarSrc: "/crew-agents/competitors.png",
    message: "ALERT: Competitor B just announced a 15% price reduction for their enterprise tier. This makes their offering approximately 7% cheaper than our comparable tier. The announcement was made 30 minutes ago via their blog and social media channels.",
    timestamp: "Monday, 2:35 PM",
    isUser: false,
    isPriority: true
  },
  {
    id: 9,
    sender: "You",
    avatarSrc: "/crew-agents/page-semantic.png",
    message: "What's their stated reason for the price reduction?",
    timestamp: "Monday, 2:40 PM",
    isUser: true
  },
  {
    id: 10,
    sender: "CompetitorBot",
    avatarSrc: "/crew-agents/competitors.png",
    message: "They're claiming it's to 'better align with the value provided' but my analysis suggests it's likely a response to slowing growth. Their last quarterly report showed a 12% reduction in new enterprise customers. Would you like me to prepare a competitive pricing analysis with recommendations for our response?",
    timestamp: "Monday, 2:43 PM",
    isUser: false
  },
  {
    id: 11,
    sender: "You",
    avatarSrc: "/crew-agents/page-semantic.png",
    message: "Yes, that would be helpful. Include options for price matching, adding value without reducing price, and targeted promotions.",
    timestamp: "Monday, 2:45 PM",
    isUser: true
  },
  {
    id: 12,
    sender: "CompetitorBot",
    avatarSrc: "/crew-agents/competitors.png",
    message: "I'll prepare that analysis right away. Also, I've just finished the implementation plan for the new dashboard features you requested earlier. You can review it at your convenience. Would you like me to share it with the product and development teams as well?",
    timestamp: "Monday, 2:50 PM",
    isUser: false
  }
];

// Pinned messages
const pinnedMessages = conversationData.filter(msg => msg.isPinned);

// Recent activity
const recentActivity = [
  {
    id: 301,
    title: "Competitive Pricing Analysis",
    timestamp: "Today, 3:30 PM",
    status: "In Progress"
  },
  {
    id: 302,
    title: "Dashboard Features Implementation Plan",
    timestamp: "Today, 2:50 PM",
    status: "Completed"
  },
  {
    id: 303,
    title: "Monthly Competitor Overview Report",
    timestamp: "Yesterday",
    status: "Completed"
  }
];

const ChatMessage = ({ message }: { message: any }) => {
  const isUser = message.isUser;
  
  return (
    <div className={`flex gap-3 mb-4 ${isUser ? 'justify-end' : ''}`}>
      {!isUser && (
        <div className="flex-shrink-0">
          <div className="relative">
            <img 
              src={message.avatarSrc} 
              alt={message.sender}
              className="h-8 w-8 rounded-full object-cover"
            />
            {message.isPriority && (
              <div className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-red-500 border-2 border-background" />
            )}
          </div>
        </div>
      )}
      
      <div className={`max-w-[70%] ${isUser ? 'bg-primary text-primary-foreground' : 'bg-muted'} rounded-lg px-4 py-2`}>
        <div className="flex justify-between items-baseline mb-1">
          <span className={`font-medium text-sm ${isUser ? 'text-primary-foreground' : 'text-foreground'}`}>
            {message.sender}
          </span>
          <span className={`text-xs ml-4 ${isUser ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
            {message.timestamp}
          </span>
        </div>
        <p className={`text-sm whitespace-pre-line ${isUser ? 'text-primary-foreground' : 'text-foreground'}`}>
          {message.message}
        </p>
        
        {message.isPinned && !isUser && (
          <div className="flex items-center gap-1 mt-2">
            <Pin className="h-3 w-3 text-yellow-500" />
            <span className="text-xs text-muted-foreground">Pinned message</span>
          </div>
        )}
      </div>
      
      {isUser && (
        <img 
          src={message.avatarSrc} 
          alt={message.sender}
          className="h-8 w-8 rounded-full object-cover mt-1"
        />
      )}
    </div>
  );
};

const ActivityItem = ({ activity }: { activity: any }) => {
  return (
    <div className="flex items-center justify-between py-2">
      <div>
        <p className="text-sm font-medium">{activity.title}</p>
        <div className="flex items-center gap-1">
          <Clock className="h-3 w-3 text-muted-foreground" />
          <p className="text-xs text-muted-foreground">{activity.timestamp}</p>
        </div>
      </div>
      <Badge 
        variant="outline" 
        className={`${
          activity.status === 'Completed' ? 'bg-green-100 text-green-800' :
          activity.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
          'bg-yellow-100 text-yellow-800'
        }`}
      >
        {activity.status}
      </Badge>
    </div>
  );
};

const CompetitorBotMessagePage = () => {
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const [conversation, setConversation] = useState(conversationData);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  
  useEffect(() => {
    document.title = "Chat with CompetitorBot | PageMind Admin";
    
    // Scroll to bottom of messages
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conversation]);

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (message.trim()) {
      // Add user message
      const newUserMessage = {
        id: conversation.length + 1,
        sender: "You",
        avatarSrc: "/crew-agents/page-semantic.png",
        message,
        timestamp: "Just now",
        isUser: true
      };
      
      setConversation([...conversation, newUserMessage]);
      setMessage("");
      
      // Simulate response after a delay (in a real app, this would be an API call)
      setTimeout(() => {
        const botResponse = {
          id: conversation.length + 2,
          sender: "CompetitorBot",
          avatarSrc: "/crew-agents/competitors.png",
          message: "Yes, I'll share the implementation plan with the product and development teams. I'll also have the competitive pricing analysis ready for you in about an hour. Is there anything specific you'd like me to focus on in the pricing analysis?",
          timestamp: "Just now",
          isUser: false
        };
        
        setConversation(prev => [...prev, botResponse]);
      }, 1000);
    }
  };

  return (
    <div className="container mx-auto p-6 h-[calc(100vh-4rem)] flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-center mb-4 pb-4 border-b">
        <div className="flex items-center">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate('/insights/messages')}
            className="mr-2"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          
          <div className="flex items-center gap-3">
            <img 
              src="/crew-agents/competitors.png" 
              alt="CompetitorBot"
              className="h-10 w-10 rounded-full object-cover"
            />
            <div>
              <div className="font-bold flex items-center gap-2">
                CompetitorBot
                <Badge variant="outline" className="text-xs">Competitor Intelligence</Badge>
                <Badge className="bg-green-500">Online</Badge>
              </div>
              <div className="text-xs text-muted-foreground">Last active: Just now</div>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            variant="outline"
            size="sm"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            Info
            {sidebarOpen ? <ChevronRight className="ml-1 h-4 w-4" /> : <ChevronLeft className="ml-1 h-4 w-4" />}
          </Button>
        </div>
      </div>
      
      {/* Main content - split into chat and sidebar */}
      <div className="flex gap-4 flex-1 overflow-hidden">
        {/* Messages Container */}
        <div className="flex-1 overflow-y-auto px-4 py-4 bg-background/80 rounded-lg">
          {pinnedMessages.length > 0 && (
            <Collapsible className="mb-4">
              <CollapsibleTrigger asChild>
                <div className="flex items-center gap-2 text-sm font-medium p-2 rounded-md bg-yellow-50 border border-yellow-200 cursor-pointer">
                  <Pin className="h-4 w-4 text-yellow-500" />
                  <span>Pinned Messages</span>
                  <ChevronDown className="h-4 w-4 ml-auto" />
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2 pl-4 border-l-2 border-yellow-200 space-y-3">
                {pinnedMessages.map(msg => (
                  <div key={msg.id} className="text-sm bg-muted p-2 rounded-md">
                    <p className="font-medium text-xs mb-1">{msg.timestamp}</p>
                    <p className="text-muted-foreground">{msg.message}</p>
                  </div>
                ))}
              </CollapsibleContent>
            </Collapsible>
          )}
          
          {conversation.map(msg => (
            <ChatMessage key={msg.id} message={msg} />
          ))}
          <div ref={messagesEndRef} />
        </div>
        
        {/* Sidebar */}
        {sidebarOpen && (
          <div className="w-64 flex-shrink-0 hidden lg:block">
            <div className="border rounded-md p-3 bg-background mb-4">
              <h3 className="font-medium mb-2">About CompetitorBot</h3>
              <p className="text-xs text-muted-foreground mb-3">
                Specialized in monitoring competitor activities, market trends, and providing strategic intelligence for competitive positioning.
              </p>
              
              <Separator className="my-3" />
              
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status:</span>
                  <span className="font-medium">Active</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Response Time:</span>
                  <span className="font-medium">~2 min</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Last Update:</span>
                  <span className="font-medium">10 min ago</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Sources:</span>
                  <span className="font-medium">15 competitors</span>
                </div>
              </div>
            </div>
            
            <div className="border rounded-md p-3 bg-background">
              <h3 className="font-medium mb-3">Recent Activity</h3>
              <div className="space-y-1 divide-y">
                {recentActivity.map(activity => (
                  <ActivityItem key={activity.id} activity={activity} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Message Input */}
      <div className="mt-4">
        <form onSubmit={handleSendMessage} className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="rounded-full"
          >
            <Paperclip className="h-4 w-4" />
          </Button>
          
          <Input
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder="Message CompetitorBot..."
            className="flex-1"
          />
          
          <Button
            type="submit"
            disabled={!message.trim()}
          >
            <Send className="h-4 w-4 mr-2" />
            Send
          </Button>
        </form>
      </div>
    </div>
  );
};

export default CompetitorBotMessagePage;